import type { JSX, ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface FieldInfoButtonProps {
  content: string;
  className?: string;
  label?: ReactNode;
}

/**
 * A help button that displays field information in a popover.
 * Uses Radix Popover's built-in accessibility and interaction handling.
 */
export function FieldInfoButton({ content, className, label }: FieldInfoButtonProps): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          className={cn(
            'inline-flex items-center gap-1 rounded-sm px-1 py-0.5 -mx-1 text-sm font-medium text-text-primary transition-colors hover:bg-neutral-50 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2',
            label ? 'cursor-pointer' : 'cursor-help',
            className
          )}
          aria-label={label ? undefined : 'Mostra dettagli campo'}
        >
          {label}
          <HelpCircle className="h-4 w-4 text-text-secondary" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="max-w-xs text-sm text-text-primary"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
